import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, unref as _unref } from "vue";
const _hoisted_1 = { class: "activity-card" };
const _hoisted_2 = {
    key: 1,
    class: "activity-card__error"
};
const _hoisted_3 = {
    key: 3,
    class: "activity-card__empty"
};
const _hoisted_4 = { class: "activity-timeline" };
const _hoisted_5 = {
    key: 0,
    class: "activity-entry"
};
const _hoisted_6 = { class: "activity-entry__rail" };
const _hoisted_7 = { class: "activity-entry__body" };
const _hoisted_8 = { class: "activity-entry__meta" };
const _hoisted_9 = { class: "activity-entry__author" };
const _hoisted_10 = {
    key: 0,
    class: "activity-entry__entity"
};
const _hoisted_11 = {
    key: 1,
    class: "activity-entry__entity"
};
const _hoisted_12 = { class: "activity-entry__time" };
const _hoisted_13 = ["onClick"];
const _hoisted_14 = { class: "activity-entry__action" };
const _hoisted_15 = { class: "activity-entry__note" };
const _hoisted_16 = { class: "activity-entry__action" };
const _hoisted_17 = { class: "activity-entry__detail" };
const _hoisted_18 = ["onClick"];
const _hoisted_19 = {
    key: 3,
    class: "activity-entry__action"
};
const _hoisted_20 = {
    key: 5,
    class: "activity-entry__action"
};
const _hoisted_21 = { class: "activity-entry__action" };
const _hoisted_22 = ["onClick"];
const _hoisted_23 = { class: "activity-entry__note" };
const _hoisted_24 = {
    key: 8,
    class: "activity-entry__action"
};
const _hoisted_25 = {
    key: 0,
    class: "activity-card__pagination"
};
import UserNote from "@/components/UserNote.vue";
import StatusNoteText from "@/components/StatusNoteText.vue";
import { ref, onMounted, computed } from "vue";
import { useDate } from "vuetify";
import { orderInfoService } from "@/services/orderInfoService";
import { intakeService } from "@/services/intakeService";
import { AuditEntry } from "@/models/audit";
import { ApiError, useApiPaginator } from "@/services/api";
import { intakeStatusLabel } from "@/models/intakeStatus";
import { pluralize, formatContactNumber } from "@/utils";
import { USER_NOTE_LEVEL } from "@/models/userNote";
import { isValidS3FileUrl } from "@/services/uploadService";
export default /*@__PURE__*/ _defineComponent({
    __name: 'AuditLogList',
    props: {
        orderInfoId: {
            type: String,
            required: false,
            default: null,
        },
        intakeOrderId: {
            type: Number,
            required: false,
            default: null,
        },
        intakeDocumentId: {
            type: String,
            required: false,
            default: null,
        },
        withNotes: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    emits: ["warning-note"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const emit = __emit;
        const props = __props;
        const entries = ref([]);
        const showLoader = ref(true);
        const errorMsg = ref(null);
        const apiMethod = props.orderInfoId
            ? orderInfoService.getOrderAuditLogs
            : props.intakeOrderId
                ? intakeService.getOrderAuditLogs
                : props.intakeDocumentId
                    ? intakeService.getIntakeDocumentAuditLogs
                    : () => Promise.resolve({ results: [] });
        const paginator = useApiPaginator(apiMethod, props.orderInfoId ?? props.intakeOrderId ?? props.intakeDocumentId);
        const hasNext = computed(() => paginator.hasNext.value);
        const hasPrevious = computed(() => paginator.hasPrevious.value);
        const dateAdapter = useDate();
        const addNote = () => {
            fetchEntries();
        };
        onMounted(async () => {
            await fetchEntries();
        });
        const fetchEntries = async () => {
            try {
                showLoader.value = true;
                const response = await paginator.fetch();
                handleEntriesFetch(response);
            }
            catch (e) {
                // TODO: handle error
                errorMsg.value = e instanceof ApiError ? e.message.toString() : "An unknown error occurred";
            }
            finally {
                showLoader.value = false;
            }
        };
        __expose({
            refresh: fetchEntries,
        });
        const fetchPage = async (isNext) => {
            try {
                showLoader.value = true;
                const response = isNext ? await paginator.next() : await paginator.previous();
                handleEntriesFetch(response);
            }
            finally {
                showLoader.value = false;
            }
        };
        const handleEntriesFetch = (response) => {
            if (response) {
                entries.value = response.results.map((entry) => new AuditEntry(entry, dateAdapter));
                // Find any warning level notes and emit the first one
                const warningsThreshold = new Date(Date.now() - 1000 * 60 * 60 * 24 * 5); // 5 days
                const warning = entries.value.find((entry) => entry.userNote?.level === "warning" && new Date(entry.userNote?.createdOn) > warningsThreshold);
                if (warning) {
                    emit("warning-note", warning);
                }
            }
            else {
                entries.value = [];
            }
        };
        const entryKind = (entry) => {
            if (entry.userNote)
                return fileNotes.value.get(entry.id) ? "file" : "note";
            if (entry.outgoingFaxTask || entry.incomingFax)
                return "fax";
            if (entry.logEntry?.action === "create")
                return "create";
            return "update";
        };
        const entryIcon = (entry) => {
            switch (entryKind(entry)) {
                case "note":
                    return "mdi-note-text-outline";
                case "file":
                    return "mdi-paperclip";
                case "fax":
                    return "mdi-fax";
                case "create":
                    return "mdi-plus";
                default:
                    return "mdi-swap-horizontal";
            }
        };
        const downloadFiles = (fileLinks) => {
            function _download(fileLink) {
                // Window.open seems to be the most reliable method
                window.open(fileLink, "_blank");
            }
            let index = 0;
            for (const fileLink of fileLinks) {
                // Space out the downloads, so the browser does not skip any files
                setTimeout(() => _download(fileLink), 200 * index++);
            }
        };
        const FILE_NOTE_REGEX = /^\[FILE:([^\]]+)\]\(([^)]+)\)$/;
        /**
         * Parses a file note from the format [FILE:filename](url) and validates
         * that the URL is a valid S3 file URL based on backend validation logic.
         * This prevents XSS attacks via javascript: or data: URLs.
         */
        const parseFileNote = (noteText) => {
            if (!noteText)
                return null;
            const match = noteText.match(FILE_NOTE_REGEX);
            if (match) {
                const fileUrl = match[2];
                // Validate the URL using the same logic as the backend S3 storage
                if (!isValidS3FileUrl(fileUrl)) {
                    return null;
                }
                return { fileName: match[1], fileUrl };
            }
            return null;
        };
        const fileNotes = computed(() => {
            const map = new Map();
            for (const entry of entries.value) {
                if (entry.userNote?.note) {
                    const parsed = parseFileNote(entry.userNote.note);
                    if (parsed) {
                        map.set(entry.id, parsed);
                    }
                }
            }
            return map;
        });
        // Shared by intake order + document audit rows — both render snooze/status the same way.
        const intakeStatusDetails = (entry) => {
            let details = [];
            const changes = entry.logEntry?.changesDict;
            if (changes?.snoozeUntil) {
                const [start, end] = changes.snoozeUntil;
                details.push(`Snooze ${start !== "None" ? new Date(start).toLocaleString() : "None"} ->` +
                    ` ${end !== "None" ? new Date(end).toLocaleString() : "None"}`);
            }
            if (changes?.status) {
                details.push(`Status ${intakeStatusLabel(changes.status[0])} -> ${intakeStatusLabel(changes.status[1])}`);
            }
            return details;
        };
        const orderInfoDetails = (entry) => {
            let details = [];
            const changes = entry.logEntry?.changesDict;
            // Handle checklist fields specially
            if (changes?.checklist) {
                const [oldChecklist, newChecklist] = changes.checklist;
                const checklistDetails = formatChecklistChanges(oldChecklist, newChecklist);
                details.push(...checklistDetails);
            }
            if (changes?.basicPlusChecklist) {
                const [oldChecklist, newChecklist] = changes.basicPlusChecklist;
                const checklistDetails = formatChecklistChanges(oldChecklist, newChecklist);
                details.push(...checklistDetails);
            }
            // Handle secondary diagnosis field specially
            if (changes?.secondaryDiagnosis) {
                const [oldDiagnosis, newDiagnosis] = changes.secondaryDiagnosis;
                const diagnosisDetails = formatSecondaryDiagnosisChanges(oldDiagnosis, newDiagnosis);
                details.push(...diagnosisDetails);
            }
            // Handle patient chart field specially (track file names, ignore URL token refreshes)
            if (changes?.patientChart) {
                const [oldChart, newChart] = changes.patientChart;
                const chartDetails = formatPatientChartChanges(oldChart, newChart);
                details.push(...chartDetails);
            }
            // Handle medical necessity field specially (track file names, ignore URL token refreshes)
            if (changes?.medicalNecessity) {
                const [oldNecessity, newNecessity] = changes.medicalNecessity;
                const necessityDetails = formatMedicalNecessityChanges(oldNecessity, newNecessity);
                details.push(...necessityDetails);
            }
            // Handle other common field changes
            const fieldsToExclude = [
                "patientChart",
                "medicalNecessity",
                "patientSsn",
                "ncitCode",
                "pharmacyInfo",
                "patientHeight",
                "patientWeight",
                "provideEmail",
                "outgoingFaxTasks",
                "notes",
                "providerDeaNumber",
                "providerSpecialty",
                "providerContactName",
                "providerOfficePhone",
                "providerFax",
                "providerTin",
                "providerTitle",
                "providerLicenseNumber",
                "insurancePlanTypeDetails",
                "patientInsuranceMemberName",
                "extraStructuredInformation",
                "filledFormUrl",
                "statusModifiedOn",
                "user",
                "checklist", // Exclude checklist from general handling since we handle it specially
                "basicPlusChecklist", // Exclude basicPlusChecklist from general handling since we handle it specially
                "secondaryDiagnosis", // Exclude secondaryDiagnosis from general handling since we handle it specially
                "staffTime",
                "staffTimeStart",
            ];
            for (const [field, [oldValue, newValue]] of Object.entries(changes || {})) {
                const nullValues = [null, undefined, "", "None", [], {}];
                // Check if both values are essentially empty/null
                const isOldEmpty = nullValues.includes(oldValue) ||
                    (Array.isArray(oldValue) && oldValue.length === 0) ||
                    (typeof oldValue === "object" && oldValue !== null && Object.keys(oldValue).length === 0);
                const isNewEmpty = nullValues.includes(newValue) ||
                    (Array.isArray(newValue) && newValue.length === 0) ||
                    (typeof newValue === "object" && newValue !== null && Object.keys(newValue).length === 0);
                if (!fieldsToExclude.includes(field) &&
                    oldValue !== newValue &&
                    !(isOldEmpty && isNewEmpty) // Don't show changes when both are empty
                ) {
                    const fieldTitle = field
                        .replace(/([A-Z])/g, " $1") // Add space before capital letters
                        .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter
                    const displayOldValue = isOldEmpty ? "Empty" : oldValue ?? "Empty";
                    const displayNewValue = isNewEmpty ? "Empty" : newValue ?? "Empty";
                    details.push(`${fieldTitle}: "${displayOldValue}" -> "${displayNewValue}"`);
                }
            }
            return details;
        };
        const formatChecklistChanges = (oldChecklist, newChecklist) => {
            const details = [];
            const oldItems = typeof oldChecklist === "string" ? JSON.parse(oldChecklist || "[]") : oldChecklist || [];
            const newItems = typeof newChecklist === "string" ? JSON.parse(newChecklist || "[]") : newChecklist || [];
            // Ensure we have arrays, not null
            const oldItemsArray = Array.isArray(oldItems) ? oldItems : [];
            const newItemsArray = Array.isArray(newItems) ? newItems : [];
            const oldMap = new Map(oldItemsArray.map((item) => [item.label, item]));
            const newMap = new Map(newItemsArray.map((item) => [item.label, item]));
            // Get all unique question labels
            const allQuestions = new Set([...oldMap.keys(), ...newMap.keys()]);
            for (const questionLabel of allQuestions) {
                const oldItem = oldMap.get(questionLabel);
                const newItem = newMap.get(questionLabel);
                const oldAnswers = oldItem?.options?.map((opt) => opt.label).join(", ") || "";
                const newAnswers = newItem?.options?.map((opt) => opt.label).join(", ") || "";
                if (!oldItem && newItem) {
                    // Question was added
                    if (newAnswers) {
                        details.push(`${questionLabel}: ${newAnswers}`);
                    }
                    else {
                        details.push(`${questionLabel} - Not applicable`);
                    }
                }
                else if (oldItem && !newItem) {
                    // Question was removed
                    details.push(`${questionLabel} - Not applicable`);
                }
                else if (oldAnswers !== newAnswers) {
                    // Question exists in both, but answers changed
                    if (oldAnswers && newAnswers) {
                        details.push(`${questionLabel}: ${oldAnswers} -> ${newAnswers}`);
                    }
                    else if (oldAnswers && !newAnswers) {
                        details.push(`${questionLabel}: ${oldAnswers} -> Not applicable`);
                    }
                    else if (!oldAnswers && newAnswers) {
                        details.push(`${questionLabel}: Not applicable -> ${newAnswers}`);
                    }
                }
            }
            return details;
        };
        const formatSecondaryDiagnosisChanges = (oldDiagnosis, newDiagnosis) => {
            const details = [];
            const oldItems = typeof oldDiagnosis === "string" ? JSON.parse(oldDiagnosis || "[]") : oldDiagnosis || [];
            const newItems = typeof newDiagnosis === "string" ? JSON.parse(newDiagnosis || "[]") : newDiagnosis || [];
            // Get the maximum length to handle all entries
            const maxLength = Math.max(oldItems.length, newItems.length);
            for (let i = 0; i < maxLength; i++) {
                const oldItem = oldItems[i] || { icd10_code: null, name: null };
                const newItem = newItems[i] || { icd10_code: null, name: null };
                const index = i + 1;
                const oldIcd10 = oldItem.icd10_code || "Empty";
                const newIcd10 = newItem.icd10_code || "Empty";
                const oldName = oldItem.name || "Empty";
                const newName = newItem.name || "Empty";
                // Check if ICD10 code changed
                if (oldIcd10 !== newIcd10) {
                    details.push(`Secondary Diagnosis: (${index}) Icd10 Code "${oldIcd10}" -> "${newIcd10}"`);
                }
                // Check if name changed
                if (oldName !== newName) {
                    details.push(`Secondary Diagnosis: (${index}) Name "${oldName}" -> "${newName}"`);
                }
            }
            return details;
        };
        // Helper function to extract filename from URL (between last slash and ?)
        const extractFilenameFromUrl = (url) => {
            if (!url || typeof url !== "string")
                return null;
            const urlWithoutParams = url.split("?")[0]; // Remove query parameters
            const urlParts = urlWithoutParams.split("/");
            return urlParts[urlParts.length - 1] || null;
        };
        const formatPatientChartChanges = (oldChart, newChart) => {
            const details = [];
            const oldItems = typeof oldChart === "string" ? JSON.parse(oldChart || "[]") : oldChart || [];
            const newItems = typeof newChart === "string" ? JSON.parse(newChart || "[]") : newChart || [];
            // Helper function to get meaningful file info (label or filename from URL)
            const getFileInfo = (item) => {
                if (!item)
                    return null;
                // Prefer label if available
                if (item.label)
                    return item.label;
                // Fallback to filename from first link
                if (item.links && item.links.length > 0) {
                    return extractFilenameFromUrl(item.links[0]);
                }
                return null;
            };
            // Get file info for all items
            const oldFileInfos = oldItems.map(getFileInfo).filter(Boolean);
            const newFileInfos = newItems.map(getFileInfo).filter(Boolean);
            // Check if files were added
            const addedFiles = newFileInfos.filter((file) => !oldFileInfos.includes(file));
            const removedFiles = oldFileInfos.filter((file) => !newFileInfos.includes(file));
            // Report added files
            addedFiles.forEach((file) => {
                details.push(`Patient Chart: Added file "${file}"`);
            });
            // Report removed files
            removedFiles.forEach((file) => {
                details.push(`Patient Chart: Removed file "${file}"`);
            });
            return details;
        };
        const formatMedicalNecessityChanges = (oldNecessity, newNecessity) => {
            const details = [];
            const oldUrls = typeof oldNecessity === "string" ? JSON.parse(oldNecessity || "[]") : oldNecessity || [];
            const newUrls = typeof newNecessity === "string" ? JSON.parse(newNecessity || "[]") : newNecessity || [];
            // Get filenames for all URLs
            const oldFileNames = oldUrls.map(extractFilenameFromUrl).filter(Boolean);
            const newFileNames = newUrls.map(extractFilenameFromUrl).filter(Boolean);
            // Check if files were added
            const addedFiles = newFileNames.filter((file) => !oldFileNames.includes(file));
            const removedFiles = oldFileNames.filter((file) => !newFileNames.includes(file));
            // Report added files
            addedFiles.forEach((file) => {
                details.push(`Medical Necessity: Added file "${file}"`);
            });
            // Report removed files
            removedFiles.forEach((file) => {
                details.push(`Medical Necessity: Removed file "${file}"`);
            });
            return details;
        };
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_btn = _resolveComponent("v-btn");
            return (_openBlock(), _createElementBlock("section", _hoisted_1, [
                _cache[11] || (_cache[11] = _createElementVNode("div", { class: "activity-card__header" }, [
                    _createElementVNode("h2", { class: "activity-card__title" }, "Logs")
                ], -1)),
                (__props.withNotes)
                    ? (_openBlock(), _createBlock(UserNote, {
                        key: 0,
                        class: "activity-card__composer",
                        title: "",
                        "order-info-id": __props.orderInfoId,
                        "intake-order-id": __props.intakeOrderId,
                        "intake-document-id": __props.intakeDocumentId,
                        onAddNote: addNote
                    }, null, 8, ["order-info-id", "intake-order-id", "intake-document-id"]))
                    : _createCommentVNode("", true),
                (errorMsg.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(errorMsg.value), 1))
                    : _createCommentVNode("", true),
                (showLoader.value)
                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                        key: 2,
                        type: "list-item-three-line",
                        class: "activity-card__loader"
                    }))
                    : (entries.value.length === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createVNode(_component_v_icon, {
                                size: "18",
                                class: "mr-2"
                            }, {
                                default: _withCtx(() => _cache[2] || (_cache[2] = [
                                    _createTextVNode("mdi-history", -1)
                                ])),
                                _: 1,
                                __: [2]
                            }),
                            _cache[3] || (_cache[3] = _createTextVNode(" No logs yet ", -1))
                        ]))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                            _createElementVNode("div", _hoisted_4, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(entries.value, (entry) => {
                                    return (_openBlock(), _createElementBlock(_Fragment, {
                                        key: entry.id
                                    }, [
                                        ((entry.orderInfo && entry.logEntry?.action === 'update' && orderInfoDetails(entry).length > 0) ||
                                            entry.userNote ||
                                            entry.outgoingFaxTask ||
                                            entry.intakeOrder ||
                                            entry.intakeDocument ||
                                            entry.incomingFax)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                                _createElementVNode("div", _hoisted_6, [
                                                    _createElementVNode("div", {
                                                        class: _normalizeClass(["activity-entry__icon", `activity-entry__icon--${entryKind(entry)}`])
                                                    }, [
                                                        _createVNode(_component_v_icon, { size: "15" }, {
                                                            default: _withCtx(() => [
                                                                _createTextVNode(_toDisplayString(entryIcon(entry)), 1)
                                                            ]),
                                                            _: 2
                                                        }, 1024)
                                                    ], 2),
                                                    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "activity-entry__line" }, null, -1))
                                                ]),
                                                _createElementVNode("div", _hoisted_7, [
                                                    _createElementVNode("div", _hoisted_8, [
                                                        _createElementVNode("span", _hoisted_9, _toDisplayString(entry.logEntry?.userFullName || "System"), 1),
                                                        (entry.intakeOrder)
                                                            ? (_openBlock(), _createElementBlock("span", _hoisted_10, "Order " + _toDisplayString(entry.intakeOrder.id), 1))
                                                            : (entry.intakeDocument)
                                                                ? (_openBlock(), _createElementBlock("span", _hoisted_11, " Doc " + _toDisplayString(entry.intakeDocument.documentKey), 1))
                                                                : _createCommentVNode("", true),
                                                        _createElementVNode("span", _hoisted_12, _toDisplayString(entry.date.toLocaleString()), 1)
                                                    ]),
                                                    (entry.userNote)
                                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                            (fileNotes.value.get(entry.id))
                                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "activity-entry__action" }, "Uploaded a file", -1)),
                                                                    _createElementVNode("button", {
                                                                        class: "activity-file-chip",
                                                                        onClick: ($event) => (downloadFiles([fileNotes.value.get(entry.id).fileUrl]))
                                                                    }, [
                                                                        _createVNode(_component_v_icon, { size: "14" }, {
                                                                            default: _withCtx(() => _cache[5] || (_cache[5] = [
                                                                                _createTextVNode("mdi-paperclip", -1)
                                                                            ])),
                                                                            _: 1,
                                                                            __: [5]
                                                                        }),
                                                                        _createElementVNode("span", null, _toDisplayString(fileNotes.value.get(entry.id).fileName), 1),
                                                                        _createVNode(_component_v_icon, { size: "13" }, {
                                                                            default: _withCtx(() => _cache[6] || (_cache[6] = [
                                                                                _createTextVNode("mdi-download", -1)
                                                                            ])),
                                                                            _: 1,
                                                                            __: [6]
                                                                        })
                                                                    ], 8, _hoisted_13)
                                                                ], 64))
                                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                                    _createElementVNode("div", _hoisted_14, _toDisplayString(entry.userNote.level === _unref(USER_NOTE_LEVEL).ARCHIVE_REASON ? "Archive reason" : "Added a note"), 1),
                                                                    _createElementVNode("div", _hoisted_15, [
                                                                        _createVNode(StatusNoteText, {
                                                                            text: entry.userNote.note
                                                                        }, null, 8, ["text"])
                                                                    ])
                                                                ], 64))
                                                        ], 64))
                                                        : (entry.outgoingFaxTask)
                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                                _createElementVNode("div", _hoisted_16, " Sent " + _toDisplayString(entry.outgoingFaxTask.description || "a fax") + " to " + _toDisplayString(_unref(formatContactNumber)(entry.outgoingFaxTask.toFaxNumber)), 1),
                                                                _createElementVNode("div", _hoisted_17, " From " + _toDisplayString(_unref(formatContactNumber)(entry.outgoingFaxTask.fromFaxNumber)) + " — status " + _toDisplayString(entry.outgoingFaxTask.status?.toLowerCase()) + " at " + _toDisplayString(entry.outgoingFaxTask.statusModifiedOn?.toLocaleString()), 1),
                                                                (entry.outgoingFaxTask.fileLinks)
                                                                    ? (_openBlock(), _createElementBlock("button", {
                                                                        key: 0,
                                                                        class: "activity-file-chip",
                                                                        onClick: ($event) => (downloadFiles(entry.outgoingFaxTask.fileLinks))
                                                                    }, [
                                                                        _createVNode(_component_v_icon, { size: "14" }, {
                                                                            default: _withCtx(() => _cache[8] || (_cache[8] = [
                                                                                _createTextVNode("mdi-download", -1)
                                                                            ])),
                                                                            _: 1,
                                                                            __: [8]
                                                                        }),
                                                                        _createElementVNode("span", null, " Download fax (" + _toDisplayString(entry.outgoingFaxTask.fileLinks.length) + " " + _toDisplayString(_unref(pluralize)(entry.outgoingFaxTask.fileLinks.length, "file", "files")) + ") ", 1)
                                                                    ], 8, _hoisted_18))
                                                                    : _createCommentVNode("", true)
                                                            ], 64))
                                                            : (entry.intakeOrder && entry.logEntry?.action === 'update')
                                                                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(intakeStatusDetails(entry), (detail) => {
                                                                    return (_openBlock(), _createElementBlock("div", {
                                                                        key: detail,
                                                                        class: "activity-entry__action"
                                                                    }, _toDisplayString(detail), 1));
                                                                }), 128))
                                                                : (entry.intakeOrder && entry.logEntry?.action === 'create')
                                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_19, "Created the order"))
                                                                    : (entry.intakeDocument && entry.logEntry?.action === 'update')
                                                                        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 4 }, _renderList(intakeStatusDetails(entry), (detail) => {
                                                                            return (_openBlock(), _createElementBlock("div", {
                                                                                key: detail,
                                                                                class: "activity-entry__action"
                                                                            }, _toDisplayString(detail), 1));
                                                                        }), 128))
                                                                        : (entry.intakeDocument && entry.logEntry?.action === 'create')
                                                                            ? (_openBlock(), _createElementBlock("div", _hoisted_20, "Created the document"))
                                                                            : (entry.incomingFax)
                                                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 6 }, [
                                                                                    _createElementVNode("div", _hoisted_21, " Received a fax from " + _toDisplayString(_unref(formatContactNumber)(entry.incomingFax.fromFaxNumber)), 1),
                                                                                    (entry.incomingFax.fileLinks)
                                                                                        ? (_openBlock(), _createElementBlock("button", {
                                                                                            key: 0,
                                                                                            class: "activity-file-chip",
                                                                                            onClick: ($event) => (downloadFiles(entry.incomingFax.fileLinks))
                                                                                        }, [
                                                                                            _createVNode(_component_v_icon, { size: "14" }, {
                                                                                                default: _withCtx(() => _cache[9] || (_cache[9] = [
                                                                                                    _createTextVNode("mdi-download", -1)
                                                                                                ])),
                                                                                                _: 1,
                                                                                                __: [9]
                                                                                            }),
                                                                                            _createElementVNode("span", null, " Download fax (" + _toDisplayString(entry.incomingFax.fileLinks.length) + " " + _toDisplayString(_unref(pluralize)(entry.incomingFax.fileLinks.length, "file", "files")) + ") ", 1)
                                                                                        ], 8, _hoisted_22))
                                                                                        : _createCommentVNode("", true)
                                                                                ], 64))
                                                                                : (entry.orderInfo && entry.logEntry?.action === 'update' && orderInfoDetails(entry).length > 0)
                                                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 7 }, [
                                                                                        _cache[10] || (_cache[10] = _createElementVNode("div", { class: "activity-entry__action" }, "Updated submission", -1)),
                                                                                        _createElementVNode("div", _hoisted_23, [
                                                                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(orderInfoDetails(entry), (detail) => {
                                                                                                return (_openBlock(), _createElementBlock("div", {
                                                                                                    key: detail,
                                                                                                    class: "activity-entry__detail"
                                                                                                }, _toDisplayString(detail), 1));
                                                                                            }), 128))
                                                                                        ])
                                                                                    ], 64))
                                                                                    : (entry.orderInfo && entry.logEntry?.action === 'create')
                                                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_24, "Created the submission"))
                                                                                        : _createCommentVNode("", true)
                                                ])
                                            ]))
                                            : _createCommentVNode("", true)
                                    ], 64));
                                }), 128))
                            ]),
                            (hasPrevious.value || hasNext.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                                    _createVNode(_component_v_btn, {
                                        icon: "mdi-chevron-left",
                                        variant: "text",
                                        size: "small",
                                        density: "comfortable",
                                        disabled: !hasPrevious.value,
                                        "aria-label": "Previous logs",
                                        onClick: _cache[0] || (_cache[0] = ($event) => (fetchPage(false)))
                                    }, null, 8, ["disabled"]),
                                    _createVNode(_component_v_btn, {
                                        icon: "mdi-chevron-right",
                                        variant: "text",
                                        size: "small",
                                        density: "comfortable",
                                        disabled: !hasNext.value,
                                        "aria-label": "Next logs",
                                        onClick: _cache[1] || (_cache[1] = ($event) => (fetchPage(true)))
                                    }, null, 8, ["disabled"])
                                ]))
                                : _createCommentVNode("", true)
                        ], 64))
            ]));
        };
    }
});
